import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Select from "react-select"
import DatePicker from "react-datepicker"
import moment from "moment"
import UserService from "services/user.service"
import { DATE } from "pages/trainings/WeeklyStats"

const service = new UserService()

const TENURE_OPTIONS = ["Rookie", "Sophomore", "Veteran"] as const

const GROUP_BY_OPTIONS = ["Leader", "Manager", "Senior Manager", "Regional", "Senior Regional", "Partner", "Division"]
const GROUP_BY_TO_API: Record<string, string> = {
  Leader: "Leader",
  Manager: "MGMT",
  "Senior Manager": "Sr MGMT",
  Regional: "Region",
  "Senior Regional": "Sr Region",
  Partner: "Partner",
  Division: "Division",
}

type CandidatesRow = {
  _id: string
  name: string
  count: number
  outreached?: number
  screening_accepted?: number
  screening_rejected?: number
  screening_no_answer?: number
  screening_follow_up?: number
  interview_accepted?: number
  interview_rejected?: number
  agreement_yes?: number
  agreement_no?: number
}

function BarCell({
  value,
  maxVal,
  colorClass = "bg-blue-500",
}: {
  value: number
  maxVal: number
  colorClass?: string
}) {
  const pct = maxVal > 0 ? (value / maxVal) * 100 : 0
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <span className="text-sm tabular-nums shrink-0 text-ink">{value.toLocaleString()}</span>
      <div className="flex-1 h-2 bg-surface-muted rounded-full overflow-hidden min-w-[50px]">
        <div className={`h-full rounded-full ${colorClass} opacity-80`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

export const CandidatesReport = () => {
  const [date, setDate] = useState<string>("YEARTODATE")
  const [showCustom, setShowCustom] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [groupBy, setGroupBy] = useState<string>("Leader")
  const [candidateStage, setCandidateStage] = useState<string>("")
  const [evoStage, setEvoStage] = useState<string>("")
  const [source, setSource] = useState<string>("")
  const [sourceCategory, setSourceCategory] = useState<string>("")
  const [recruitingLocation, setRecruitingLocation] = useState<string>("")
  const [salesLeaderId, setSalesLeaderId] = useState<string>("")
  const [tenure, setTenure] = useState<string[]>([])

  const [filterOptions, setFilterOptions] = useState<{
    candidateStages: string[]
    evoStages: string[]
    sources: string[]
    sourceCategories: string[]
    recruitingLocations: string[]
  }>({
    candidateStages: [],
    evoStages: [],
    sources: [],
    sourceCategories: [],
    recruitingLocations: [],
  })
  const [salesLeadersFromServer, setSalesLeadersFromServer] = useState<{ id: string; name: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [apiData, setApiData] = useState<Record<string, CandidatesRow[]> | null>(null)

  const groupByApi = groupBy ? (GROUP_BY_TO_API[groupBy] ?? "") : ""

  const salesLeaderOptions = useMemo(
    () =>
      salesLeadersFromServer.map((s) => ({
        value: s.id,
        label: s.name ? `${s.name} (${s.id})` : s.id,
      })),
    [salesLeadersFromServer]
  )

  const tenureSelectOptions = useMemo(
    () => TENURE_OPTIONS.map((t) => ({ value: t, label: t })),
    []
  )
  const selectedTenures = useMemo(
    () => tenureSelectOptions.filter((o) => tenure.includes(o.value)),
    [tenureSelectOptions, tenure]
  )

  const candidateStageOptions = useMemo(
    () => [{ value: "", label: "All" }, ...filterOptions.candidateStages.map((s) => ({ value: s, label: s }))],
    [filterOptions.candidateStages]
  )
  const evoStageOptions = useMemo(
    () => [{ value: "", label: "All" }, ...filterOptions.evoStages.map((s) => ({ value: s, label: s }))],
    [filterOptions.evoStages]
  )
  const sourceOptions = useMemo(
    () => [{ value: "", label: "All" }, ...filterOptions.sources.map((s) => ({ value: s, label: s }))],
    [filterOptions.sources]
  )
  const sourceCategoryOptions = useMemo(
    () =>
      [{ value: "", label: "All" }, ...filterOptions.sourceCategories.map((s) => ({ value: s, label: s }))],
    [filterOptions.sourceCategories]
  )
  const recruitingLocationOptions = useMemo(
    () =>
      [
        { value: "", label: "All" },
        ...filterOptions.recruitingLocations.map((s) => ({ value: s, label: s })),
      ],
    [filterOptions.recruitingLocations]
  )

  const queryParams = useMemo(
    () => ({
      date: date || undefined,
      groupBy: groupByApi || undefined,
      candidateStage: candidateStage.trim() || undefined,
      evoStage: evoStage.trim() || undefined,
      source: source.trim() || undefined,
      sourceCategory: sourceCategory.trim() || undefined,
      recruitingLocation: recruitingLocation.trim() || undefined,
      salesLeaderId: salesLeaderId || undefined,
      tenure: tenure.length > 0 ? tenure.join(",") : undefined,
    }),
    [date, groupByApi, candidateStage, evoStage, source, sourceCategory, recruitingLocation, salesLeaderId, tenure]
  )

  const fetchReport = useCallback(() => {
    if (!groupByApi) return
    setLoading(true)
    service
      .getCandidatesRegionalReporting(queryParams)
      .then((resp: any) => {
        if (resp?.data?.success && resp?.data?.data) setApiData(resp.data.data)
        else setApiData(null)
      })
      .catch(() => setApiData(null))
      .finally(() => setLoading(false))
  }, [queryParams, groupByApi])

  const fetchSalesLeaders = useCallback(() => {
    service
      .getCandidatesSalesLeaders()
      .then((resp: any) => {
        const list = resp?.data?.data ?? resp?.data
        if (Array.isArray(list)) {
          setSalesLeadersFromServer(list.map((x: any) => ({ id: x.id ?? x._id ?? "", name: x.name ?? "" })))
        } else {
          setSalesLeadersFromServer([])
        }
      })
      .catch(() => setSalesLeadersFromServer([]))
  }, [])

  const fetchFilterOptions = useCallback(() => {
    service
      .getCandidatesFilterOptions()
      .then((resp: any) => {
        const d = resp?.data?.data ?? resp?.data ?? {}
        setFilterOptions({
          candidateStages: Array.isArray(d.candidateStages) ? d.candidateStages : [],
          evoStages: Array.isArray(d.evoStages) ? d.evoStages : [],
          sources: Array.isArray(d.sources) ? d.sources : [],
          sourceCategories: Array.isArray(d.sourceCategories) ? d.sourceCategories : [],
          recruitingLocations: Array.isArray(d.recruitingLocations) ? d.recruitingLocations : [],
        })
      })
      .catch(() => setFilterOptions((p) => ({ ...p })))
  }, [])

  const initialFetchDone = useRef(false)
  useEffect(() => {
    if (!initialFetchDone.current && groupByApi) {
      initialFetchDone.current = true
      fetchReport()
    }
  }, [groupByApi, fetchReport])

  useEffect(() => {
    fetchSalesLeaders()
    fetchFilterOptions()
  }, [fetchSalesLeaders, fetchFilterOptions])

  const { tableRows, dataGroupBy } = useMemo(() => {
    if (!apiData) return { tableRows: [] as CandidatesRow[], dataGroupBy: null }
    const keyForCurrent = groupByApi ? `by_${groupByApi}` : null
    const arr = keyForCurrent ? apiData[keyForCurrent] : undefined
    if (Array.isArray(arr) && arr.length > 0) {
      return { tableRows: arr, dataGroupBy: groupByApi }
    }
    const firstKey = Object.keys(apiData)[0]
    const fallback = firstKey && Array.isArray(apiData[firstKey]) ? apiData[firstKey] : []
    const fallbackGroupBy = firstKey ? firstKey.replace(/^by_/, "") : null
    return { tableRows: fallback, dataGroupBy: fallbackGroupBy }
  }, [apiData, groupByApi])

  const maxCount = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.count), 1) : 1), [tableRows])
  const maxOutreached = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.outreached ?? 0), 1) : 1),
    [tableRows]
  )
  const maxScreeningAcc = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.screening_accepted ?? 0), 1) : 1),
    [tableRows]
  )
  const maxScreeningRej = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.screening_rejected ?? 0), 1) : 1),
    [tableRows]
  )
  const maxScreeningNo = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.screening_no_answer ?? 0), 1) : 1),
    [tableRows]
  )
  const maxScreeningFup = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.screening_follow_up ?? 0), 1) : 1),
    [tableRows]
  )
  const maxInterviewAcc = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.interview_accepted ?? 0), 1) : 1),
    [tableRows]
  )
  const maxInterviewRej = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.interview_rejected ?? 0), 1) : 1),
    [tableRows]
  )
  const maxAgreeYes = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.agreement_yes ?? 0), 1) : 1),
    [tableRows]
  )
  const maxAgreeNo = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.agreement_no ?? 0), 1) : 1),
    [tableRows]
  )

  const totals = useMemo(
    () => ({
      count: tableRows.reduce((s, r) => s + (r.count ?? 0), 0),
      outreached: tableRows.reduce((s, r) => s + (r.outreached ?? 0), 0),
      screening_accepted: tableRows.reduce((s, r) => s + (r.screening_accepted ?? 0), 0),
      screening_rejected: tableRows.reduce((s, r) => s + (r.screening_rejected ?? 0), 0),
      screening_no_answer: tableRows.reduce((s, r) => s + (r.screening_no_answer ?? 0), 0),
      screening_follow_up: tableRows.reduce((s, r) => s + (r.screening_follow_up ?? 0), 0),
      interview_accepted: tableRows.reduce((s, r) => s + (r.interview_accepted ?? 0), 0),
      interview_rejected: tableRows.reduce((s, r) => s + (r.interview_rejected ?? 0), 0),
      agreement_yes: tableRows.reduce((s, r) => s + (r.agreement_yes ?? 0), 0),
      agreement_no: tableRows.reduce((s, r) => s + (r.agreement_no ?? 0), 0),
    }),
    [tableRows]
  )

  const handleChangeDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setStartDate(start ?? null)
    setEndDate(end ?? null)
    if (start && end) {
      setDate(`${moment(start).format("YYYY-MM-DD")},${moment(end).format("YYYY-MM-DD")}`)
    }
  }

  const colCount = 14

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="page-title">Candidates Report</h1>
        <p className="page-subtitle">Candidate counts by date range and group, with optional filters.</p>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Date range</label>
            <select
              value={date}
              onChange={(e) => {
                if (e.target.value !== "CUSTOM") setDate(e.target.value)
                else setShowCustom(true)
              }}
              className="px-3 py-2 text-sm"
            >
              {DATE.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {showCustom && (
              <DatePicker
                selected={startDate}
                startDate={startDate}
                endDate={endDate}
                onChange={handleChangeDates}
                selectsRange
                isClearable
                placeholderText="Select range"
                className="z-[100] mt-2 py-2 w-56 rounded-lg border border-line bg-white text-ink px-3 text-sm"
                customInput={<input placeholder="Select date" className="rounded-lg border border-line bg-white text-ink w-56 py-2 px-3 text-sm" />}
              />
            )}
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Group by</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {GROUP_BY_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[160px]">
            <label className="field-label">Candidate stage</label>
            <select
              value={candidateStage}
              onChange={(e) => setCandidateStage(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {candidateStageOptions.map((o) => (
                <option key={o.value || "all"} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[160px]">
            <label className="field-label">Evo stage</label>
            <select
              value={evoStage}
              onChange={(e) => setEvoStage(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {evoStageOptions.map((o) => (
                <option key={o.value || "all"} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="field-label">Source</label>
            <Select
              isClearable
              isSearchable
              placeholder="All..."
              value={sourceOptions.find((o) => o.value === source) ?? null}
              options={sourceOptions}
              onChange={(opt) => setSource(opt?.value ?? "")}
              className="react-select min-w-[180px]"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#e6e8ec", boxShadow: "none" }),
                menu: (base) => ({ ...base, backgroundColor: "#fff" }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? "#f6f7f9" : "transparent",
                  color: "#111827",
                }),
                singleValue: (base) => ({ ...base, color: "#111827" }),
                input: (base) => ({ ...base, color: "#111827" }),
              }}
            />
          </div>

          <div className="flex flex-col gap-1 min-w-[160px]">
            <label className="field-label">Source category</label>
            <select
              value={sourceCategory}
              onChange={(e) => setSourceCategory(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {sourceCategoryOptions.map((o) => (
                <option key={o.value || "all"} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="field-label">Recruiting location</label>
            <Select
              isClearable
              isSearchable
              placeholder="All..."
              value={recruitingLocationOptions.find((o) => o.value === recruitingLocation) ?? null}
              options={recruitingLocationOptions}
              onChange={(opt) => setRecruitingLocation(opt?.value ?? "")}
              className="react-select min-w-[180px]"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#e6e8ec", boxShadow: "none" }),
                menu: (base) => ({ ...base, backgroundColor: "#fff" }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? "#f6f7f9" : "transparent",
                  color: "#111827",
                }),
                singleValue: (base) => ({ ...base, color: "#111827" }),
                input: (base) => ({ ...base, color: "#111827" }),
              }}
            />
          </div>

          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="field-label">Tenure (signed rep)</label>
            <Select
              isMulti
              isClearable
              isSearchable={false}
              placeholder="All tenures…"
              value={selectedTenures}
              options={tenureSelectOptions}
              onChange={(opts) => setTenure(opts ? opts.map((o) => o.value) : [])}
              className="react-select min-w-[200px]"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#e6e8ec", boxShadow: "none" }),
                menu: (base) => ({ ...base, backgroundColor: "#fff" }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? "#f6f7f9" : "transparent",
                  color: "#111827",
                }),
                multiValue: (base) => ({ ...base, backgroundColor: "#f1f3f5" }),
                multiValueLabel: (base) => ({ ...base, color: "#111827" }),
                multiValueRemove: (base) => ({ ...base, color: "#111827" }),
                input: (base) => ({ ...base, color: "#111827" }),
              }}
            />
          </div>

          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="field-label">Sales leader</label>
            <Select
              isClearable
              isSearchable
              placeholder="All..."
              value={salesLeaderOptions.find((o) => o.value === salesLeaderId) ?? null}
              options={salesLeaderOptions}
              onChange={(opt) => setSalesLeaderId(opt?.value ?? "")}
              onMenuOpen={fetchSalesLeaders}
              className="react-select min-w-[180px]"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#e6e8ec", boxShadow: "none" }),
                menu: (base) => ({ ...base, backgroundColor: "#fff" }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? "#f6f7f9" : "transparent",
                  color: "#111827",
                }),
                singleValue: (base) => ({ ...base, color: "#111827" }),
                input: (base) => ({ ...base, color: "#111827" }),
              }}
            />
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label invisible">Search</label>
            <button
              type="button"
              onClick={fetchReport}
              disabled={!groupByApi || loading}
              className="btn-primary"
            >
              {loading ? "Loading…" : "Search"}
            </button>
          </div>
        </div>
      </div>

      {dataGroupBy && groupByApi && dataGroupBy !== groupByApi && tableRows.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 text-amber-800 text-sm">
          Showing data for <strong>{dataGroupBy}</strong>. Click Search to load data for <strong>{groupByApi}</strong>.
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-line">
          <h2 className="card-title">Candidates by {groupBy || "Group"}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface text-ink-muted text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Name</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Count</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Outreached</th>
                <th colSpan={4} className="px-2 py-2.5 font-medium text-center border-l border-line">
                  Screening result
                </th>
                <th colSpan={2} className="px-2 py-2.5 font-medium text-center border-l border-line">
                  Interview result
                </th>
                <th colSpan={2} className="px-2 py-2.5 font-medium text-center border-l border-line">
                  Agreement signed
                </th>
              </tr>
              <tr className="border-b border-line text-ink-muted text-xs uppercase">
                <th className="px-2 py-2" />
                <th className="px-2 py-2" />
                <th className="px-2 py-2" />
                <th className="px-2 py-2 border-l border-line">Accepted</th>
                <th className="px-2 py-2">Rejected</th>
                <th className="px-2 py-2">No answer</th>
                <th className="px-2 py-2">Follow up</th>
                <th className="px-2 py-2 border-l border-line">Accepted</th>
                <th className="px-2 py-2">Rejected</th>
                <th className="px-2 py-2 border-l border-line">Yes</th>
                <th className="px-2 py-2">No</th>
              </tr>
            </thead>
            <tbody>
              {loading && !apiData ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-8 text-center text-ink-muted">Loading...</td>
                </tr>
              ) : !tableRows.length ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-8 text-center text-ink-muted">
                    {apiData ? "No data for the selected filters." : "Select group by and click Search."}
                  </td>
                </tr>
              ) : (
                tableRows.map((row, idx) => (
                  <tr
                    key={row._id != null ? String(row._id) : `row-${idx}`}
                    className="border-b border-line hover:bg-surface transition-colors"
                  >
                    <td className="px-2 py-3 text-ink text-sm whitespace-nowrap">{row.name ?? row._id ?? "—"}</td>
                    <td className="px-2 py-3">
                      <BarCell value={row.count} maxVal={maxCount} colorClass="bg-blue-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.outreached ?? 0} maxVal={maxOutreached} colorClass="bg-cyan-500" />
                    </td>
                    <td className="px-2 py-3 border-l border-line">
                      <BarCell value={row.screening_accepted ?? 0} maxVal={maxScreeningAcc} colorClass="bg-emerald-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.screening_rejected ?? 0} maxVal={maxScreeningRej} colorClass="bg-rose-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.screening_no_answer ?? 0} maxVal={maxScreeningNo} colorClass="bg-amber-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.screening_follow_up ?? 0} maxVal={maxScreeningFup} colorClass="bg-violet-500" />
                    </td>
                    <td className="px-2 py-3 border-l border-line">
                      <BarCell value={row.interview_accepted ?? 0} maxVal={maxInterviewAcc} colorClass="bg-emerald-600" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.interview_rejected ?? 0} maxVal={maxInterviewRej} colorClass="bg-rose-600" />
                    </td>
                    <td className="px-2 py-3 border-l border-line">
                      <BarCell value={row.agreement_yes ?? 0} maxVal={maxAgreeYes} colorClass="bg-teal-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.agreement_no ?? 0} maxVal={maxAgreeNo} colorClass="bg-gray-500" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-line bg-surface text-ink font-semibold text-sm tabular-nums">
                <td className="px-2 py-3 whitespace-nowrap">Total</td>
                <td className="px-2 py-3">{totals.count.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.outreached.toLocaleString()}</td>
                <td className="px-2 py-3 border-l border-line">{totals.screening_accepted.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.screening_rejected.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.screening_no_answer.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.screening_follow_up.toLocaleString()}</td>
                <td className="px-2 py-3 border-l border-line">{totals.interview_accepted.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.interview_rejected.toLocaleString()}</td>
                <td className="px-2 py-3 border-l border-line">{totals.agreement_yes.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.agreement_no.toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  )
}
